import { useQuery } from "@tanstack/react-query";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { authHeader } from "@/utils/headers";

/**
 * List files in a vector store with full details
 * First fetches file IDs from the vector store, then gets full file details
 */
export const useVectorStoreFiles = (vector_store_id?: string) => {
  const auth = useAuth();
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId;

  return useQuery({
    queryKey: ["vector-store-files", vector_store_id, workspaceId],
    queryFn: async () => {
      const fileIdsResponse = await fetch(
        `${API_URL}/api/dashboard/v2/vector-store/${vector_store_id}/files?workspace_id=${workspaceId}`,
        {
          headers: authHeader(auth?.userToken ?? ""),
        },
      ).then(handleResponse);

      // Check if response has success: false
      if (fileIdsResponse && typeof fileIdsResponse === "object" && "success" in fileIdsResponse) {
        if (fileIdsResponse.success === false) {
          const errorMessage =
            (fileIdsResponse as { message?: string }).message ||
            "Failed to fetch vector store files";
          throw new Error(errorMessage);
        }
      }

      const fileIds = fileIdsResponse?.data?.map((f: { id: string }) => f.id) || [];

      if (fileIds.length === 0) {
        return { success: true, data: [] };
      }

      const idsParam = fileIds.map((id: string) => `ids[]=${id}`).join("&");
      const filesResponse = await fetch(
        `${API_URL}/api/dashboard/v2/vector-store/files/list?workspace_id=${workspaceId}&${idsParam}`,
        {
          headers: authHeader(auth?.userToken ?? ""),
        },
      ).then(handleResponse);

      // Check if response has success: false
      if (filesResponse && typeof filesResponse === "object" && "success" in filesResponse) {
        if (filesResponse.success === false) {
          const errorMessage =
            (filesResponse as { message?: string }).message || "Failed to fetch file details";
          throw new Error(errorMessage);
        }
      }

      return filesResponse;
    },
    enabled: !!vector_store_id && !!workspaceId,
  });
};
